import React, { useMemo } from 'react';
import { AlertTriangle, CheckCircle, FileJson, AlertOctagon, RefreshCw } from 'lucide-react';
import { validateQuestionData } from '../../utils/jsonValidator';
import { Card } from '../ui/Card';
import { Question } from '../../types';
interface ValidationPanelProps {
  questions: Question[];
}
export function ValidationPanel({
  questions
}: ValidationPanelProps) {
  const report = useMemo(() => validateQuestionData(), [questions]);
  const hasErrors = report.orphanedQuestions.length > 0 || report.missingFields.length > 0 || report.duplicateIds.length > 0;
  const migration = report.migrationReadiness;
  return <div className="space-y-6" data-id="element-918">
      <div className="flex items-center justify-between" data-id="element-919">
        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2" data-id="element-920">
          <FileJson className="w-5 h-5 text-indigo-600" data-id="element-921" />
          Data Integrity Report
        </h3>
        <span className={`px-3 py-1 rounded-full text-sm font-bold ${hasErrors ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`} data-id="element-922">
          {hasErrors ? 'Issues Found' : 'All Systems Go'}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-923">
        <StatCard label="Total Questions" value={report.totalQuestions} icon={<CheckCircle className="w-5 h-5 text-emerald-500" data-id="element-925" />} data-id="element-924" />
        <StatCard label="Duplicates" value={report.duplicateIds.length} icon={<AlertOctagon className="w-5 h-5 text-red-500" data-id="element-927" />} isError={report.duplicateIds.length > 0} data-id="element-926" />
        <StatCard label="Missing Fields" value={report.missingFields.length} icon={<AlertTriangle className="w-5 h-5 text-amber-500" data-id="element-929" />} isError={report.missingFields.length > 0} data-id="element-928" />
        <StatCard label="Orphaned" value={report.orphanedQuestions.length} icon={<AlertTriangle className="w-5 h-5 text-amber-500" data-id="element-931" />} isError={report.orphanedQuestions.length > 0} data-id="element-930" />
      </div>

      {report.missingFields.length > 0 && <Card className="p-4 border-red-200 bg-red-50" data-id="element-932">
          <h4 className="font-bold text-red-800 mb-2" data-id="element-933">Missing Fields Detail</h4>
          <div className="max-h-40 overflow-y-auto text-sm text-red-700 space-y-1" data-id="element-934">
            {report.missingFields.map((item, i) => <div key={i} className="font-mono" data-id="element-935">
                {item.id}: {item.fields.join(', ')}
              </div>)}
          </div>
        </Card>}

      {/* 🔄 MIGRATION: v2.0 Schema Migration Readiness */}
      <Card className="p-4 border-blue-200 bg-blue-50/50" data-id="element-936">
        <h4 className="font-bold text-blue-900 mb-3 flex items-center gap-2" data-id="element-937">
          <RefreshCw className="w-4 h-4 text-blue-600" data-id="element-938" />
          v2.0 Schema Migration
        </h4>

        {/* Overall progress bar */}
        <div className="mb-4" data-id="element-939">
          <div className="flex justify-between text-sm mb-1" data-id="element-940">
            <span className="text-blue-700 font-medium" data-id="element-941">Overall Progress</span>
            <span className="text-blue-900 font-bold" data-id="element-942">
              {migration.overallPercent}%
            </span>
          </div>
          <div className="w-full bg-blue-100 rounded-full h-2.5" data-id="element-943">
            <div className="bg-blue-600 h-2.5 rounded-full transition-all" style={{
            width: `${migration.overallPercent}%`
          }} data-id="element-944" />
          </div>
        </div>

        {/* Field coverage stats */}
        <div className="grid grid-cols-2 gap-3 mb-4 text-sm" data-id="element-945">
          <MigrationStat label="With conceptId" value={migration.withConceptId} total={migration.totalQuestions} data-id="element-946" />
          <MigrationStat label="With variantIndex" value={migration.withVariantIndex} total={migration.totalQuestions} data-id="element-947" />
          <MigrationStat label="Missing familyId" value={migration.missingFamilyId} total={migration.totalQuestions} isWarning data-id="element-948" />
          <MigrationStat label="Tier mismatches" value={migration.tierMismatches.length} total={migration.totalQuestions} isWarning data-id="element-949" />
        </div>

        {/* Per-module progress */}
        <div className="space-y-2" data-id="element-950">
          <h5 className="text-xs font-semibold text-blue-800 uppercase tracking-wide" data-id="element-951">
            Per Module
          </h5>
          {Object.entries(migration.byModule).map(([mod, data]) => <div key={mod} className="flex items-center gap-3 text-sm" data-id="element-952">
              <span className="text-blue-700 w-28 truncate" data-id="element-953">{mod}</span>
              <div className="flex-1 bg-blue-100 rounded-full h-1.5" data-id="element-954">
                <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{
              width: `${data.percentComplete}%`
            }} data-id="element-955" />
              </div>
              <span className="text-blue-900 font-mono text-xs w-16 text-right" data-id="element-956">
                {data.fullyMigrated}/{data.total}
              </span>
            </div>)}
        </div>

        {migration.tierMismatches.length > 0 && <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-957">
            <h5 className="text-xs font-semibold text-amber-800 mb-1" data-id="element-958">
              ⚠️ Tier Mismatches
            </h5>
            <div className="max-h-24 overflow-y-auto text-xs text-amber-700 space-y-0.5" data-id="element-959">
              {migration.tierMismatches.map((m, i) => <div key={i} className="font-mono" data-id="element-960">
                  {m.id}: explicit={m.explicit} vs derived={m.derived}
                </div>)}
            </div>
          </div>}
      </Card>

      <Card className="p-4" data-id="element-961">
        <h4 className="font-bold text-slate-900 mb-4" data-id="element-962">
          Distribution per Module
        </h4>
        <div className="space-y-2" data-id="element-963">
          {Object.entries(report.moduleStats).map(([mod, count]) => <div key={mod} className="flex justify-between items-center text-sm" data-id="element-964">
              <span className="text-slate-600" data-id="element-965">{mod}</span>
              <span className="font-mono font-bold text-slate-900" data-id="element-966">
                {count}
              </span>
            </div>)}
        </div>
      </Card>
    </div>;
}
function StatCard({
  label,
  value,
  icon,
  isError = false
}: any) {
  return <div className={`p-4 rounded-xl border ${isError ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`} data-id="element-967">
      <div className="flex items-center justify-between mb-1" data-id="element-968">
        <span className={`text-sm font-medium ${isError ? 'text-red-600' : 'text-slate-500'}`} data-id="element-969">
          {label}
        </span>
        {icon}
      </div>
      <div className={`text-2xl font-bold ${isError ? 'text-red-900' : 'text-slate-900'}`} data-id="element-970">
        {value}
      </div>
    </div>;
}
function MigrationStat({
  label,
  value,
  total,
  isWarning = false
}: {
  label: string;
  value: number;
  total: number;
  isWarning?: boolean;
}) {
  return <div className={`p-2 rounded-lg ${isWarning && value > 0 ? 'bg-amber-50' : 'bg-white/60'}`} data-id="element-971">
      <div className={`text-xs ${isWarning && value > 0 ? 'text-amber-600' : 'text-blue-600'}`} data-id="element-972">
        {label}
      </div>
      <div className={`font-bold ${isWarning && value > 0 ? 'text-amber-900' : 'text-blue-900'}`} data-id="element-973">
        {value}
        <span className="text-xs font-normal opacity-60" data-id="element-974">/{total}</span>
      </div>
    </div>;
}